<template>
    <div>
        <limitless-page-header :loading="busy_loading" :title="'Skördeplaner'" :sub-title="plan_name"></limitless-page-header>

        <div class="content">
            <tb-skeleton v-if="busy_loading" :height="100" :theme="'opacity'" :shape="'radius'" bg-color="#dcdbdc"></tb-skeleton>

            <div v-else-if="!busy_loading && plan">
                <ValidationObserver ref="planform">
                    <form @submit.prevent="savePlan">
                        <div class="row">
                            <div class="col-lg-6">
                                <div class="card">
                                    <div class="card-header bg-white">
                                        <h5 class="card-title">Skördeplan</h5>
                                    </div>
                                    <div class="card-body">
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Enhet:</label>
                                            <div class="col-lg-10">
                                                <div class="form-group">
                                                    <input v-if="this.planId > 0" type="text" class="form-control" :value="(unit ? unit.name : '')" disabled>
                                                    <v-select
                                                        v-else
                                                        v-model="unit"
                                                        label="name"
                                                        :options="units"
                                                        :clearable="false"
                                                        placeholder="Välj enhet..."
                                                    >
                                                    </v-select>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Namn:</label>
                                            <div class="col-lg-10">
                                                <div class="form-group">
                                                    <input type="text" class="form-control" v-model="plan.name">
                                                </div>
                                            </div>
                                        </div>
                                        <div class="row">
                                            <label class="col-form-label col-lg-2">Startdatum: <span class="text-danger">*</span></label>
                                            <div class="col-lg-10">
                                                <ValidationProvider vid="start_date" name="Startdatum" rules="required" v-slot="{ errors }">
                                                    <v-date-picker
                                                        v-model="plan.start_date"
                                                        mode="date"
                                                        :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD' } }"
                                                        :disabled-dates='{ weekdays: [1, 3, 4, 5, 6, 7] }'
                                                        :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                                                        show-iso-weeknumbers
                                                    >
                                                        <template v-slot="{ inputValue, togglePopover }">
                                                            <input class="form-control" :value="inputValue" @focus="togglePopover()" v-bind:class="{ 'border-danger':errors[0] }">
                                                        </template>
                                                    </v-date-picker>
                                                    <span class="validation-invalid-label">{{ errors[0] }}</span>
                                                    <span class="text-muted">Datum för första skörd</span>
                                                </ValidationProvider>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="card-footer">
                                        <div class="d-flex">
                                            <limitless-button ref="btndeleteplan" type="button" @click="deletePlan" :busy="busy_saving" icon="icon-cross2" btn-class="btn-danger" class="mr-auto">Radera</limitless-button>
                                            <limitless-button ref="btnmodalcopyplan" type="button" @click="showModalCopyPlan" :busy="busy_saving" icon="icon-copy4" btn-class="btn-light" class="mr-3">Kopiera</limitless-button>
                                            <limitless-button ref="btnsaveplan" type="button" @click="savePlan" :busy="busy_saving" icon="icon-floppy-disk">Spara</limitless-button>
                                        </div>
                                    </div>
                                </div> <!-- /card -->
                            </div> <!-- /col -->
                            <div class="col-lg-6">
                            </div>
                        </div> <!-- /row -->

                        <div class="row">
                            <div class="col-12">
                                <div class="card">
                                    <div class="card-header bg-white">
                                        <h5 class="card-title">Skördeplan</h5>
                                    </div>
                                    <div v-if="unit && unit.layers.length > 0" class="card-table" style="border-bottom-style: solid; border-bottom-color: #186632 !important; border-bottom-width: 2px;">
                                        <table class="table">
                                            <tbody v-for="layer in unit.layers" :key="'layer-' + layer.id">
                                                <tr style="border-top-style: solid; border-top-color: #186632 !important; border-top-width: 2px;">
                                                    <th colspan="9"><span style="font-size: 1rem;">{{ layer.title }}</span><br><span class="text-muted">{{ layer.gutters }} brickor med {{ layer.plugs }} pluggar</span></th>
                                                </tr>
                                                <template v-if="crops.length > 0 && plan.crops_weekplan[layer.id]">
                                                    <tr>
                                                        <th></th>
                                                        <th class="px-1">Måndag</th>
                                                        <th class="px-1">Tisdag</th>
                                                        <th class="px-1">Onsdag</th>
                                                        <th class="px-1">Torsdag</th>
                                                        <th class="px-1">Fredag</th>
                                                        <th class="px-1">Lördag</th>
                                                        <th class="px-1">Söndag</th>
                                                        <th class="text-right">Totalt</th>
                                                    </tr>
                                                    <template v-for="(crop) in crops">
                                                        <tr v-if="crop.id in plan.crops_weekplan[layer.id]" :key="'crop-' + layer.id + '-' + crop.id">
                                                            <th>{{ crop.name }}</th>
                                                            <td v-for="n in 7" :key="'crop-' + layer.id + '-' + crop.id + '-' + n" style="vertical-align: top;" class="px-1">
                                                                <input type="number" min="0" step="1" class="form-control" v-model="plan.crops_weekplan[layer.id][crop.id][n]">
                                                                <span v-if="plan.crops_weekplan[layer.id][crop.id][n] > 0 && weekday_crop_sowdates[crop.id][n].sow_date_passed" class="text-danger">
                                                                    Första sådatum passerat
                                                                </span>
                                                            </td>
                                                            <th class="text-right">{{ layer_crop_count[layer.id] ? layer_crop_count[layer.id]['crops'][crop.id] : 0 }}</th>
                                                        </tr>
                                                    </template>
                                                </template>
                                                <template v-else>
                                                    <tr>
                                                        <th colspan="9">Inga grödor på detta lager</th>
                                                    </tr>
                                                </template>
                                                <tr>
                                                    <td colspan="9">
                                                        <div style="display: flex; align-items: center;">
                                                            <div class="mr-2">
                                                                Lägg till:
                                                            </div>
                                                            <div style="width: 250px;">
                                                                <v-select
                                                                    v-model="add_crop"
                                                                    label="name"
                                                                    :options="crops"
                                                                    :clearable="false"
                                                                    @input="addCrop($event, layer.id)"
                                                                    placeholder="Add crop..."
                                                                    :selectable="(option) => !(plan.crops_weekplan[layer.id] && option.id in plan.crops_weekplan[layer.id])"
                                                                >
                                                                    <template slot="no-options">
                                                                        Type to search...
                                                                    </template>
                                                                    <template slot="option" slot-scope="option">
                                                                        <div>
                                                                            {{ option.code }} - {{ option.name }}
                                                                        </div>
                                                                    </template>
                                                                    <template slot="selected-option" slot-scope="option">
                                                                        <div class="selected">
                                                                            {{ option.code }} - {{ option.name }}
                                                                        </div>
                                                                    </template>
                                                                </v-select>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr v-if="layer_crop_count[layer.id]">
                                                    <th></th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][1] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][2] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][3] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][4] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][5] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][6] }}</th>
                                                    <th>{{ layer_crop_count[layer.id]['days'][7] }}</th>
                                                    <th class="text-right" :class="{ 'text-danger': layer_crop_count[layer.id].count > (layer.gutters * layer.plugs / (unit.days_in_system / 7)) }">
                                                        {{ layer_crop_count[layer.id].count }} av {{ (layer.gutters * layer.plugs / (unit.days_in_system / 7)) }}
                                                    </th>
                                                </tr>
                                            </tbody>
                                            <tfoot>
                                                <tr v-if="total_crop_count" style="border-top-style: solid; border-top-color: #186632 !important; border-top-width: 2px;">
                                                    <th>Totalt:</th>
                                                    <th>{{ total_crop_count['days'][1] }}</th>
                                                    <th>{{ total_crop_count['days'][2] }}</th>
                                                    <th>{{ total_crop_count['days'][3] }}</th>
                                                    <th>{{ total_crop_count['days'][4] }}</th>
                                                    <th>{{ total_crop_count['days'][5] }}</th>
                                                    <th>{{ total_crop_count['days'][6] }}</th>
                                                    <th>{{ total_crop_count['days'][7] }}</th>
                                                    <th class="text-right" :class="{ 'text-danger': total_crop_count.count > total_crop_count.available_slots }">
                                                        {{ total_crop_count.count }} av {{ total_crop_count.available_slots }}
                                                    </th>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                    <div v-else class="card-body">
                                        <i>Hittade inga lager för denna enhet...</i>
                                    </div>
                                    <div class="card-footer">
                                        <div class="d-flex justify-content-end">
                                            <limitless-button ref="btnsaveplan" type="button" @click="savePlan" :busy="busy_saving" icon="icon-floppy-disk">Spara</limitless-button>
                                        </div>
                                    </div>
                                </div> <!-- /card -->
                            </div>
                        </div>
                    </form>
                </ValidationObserver>
            </div>
        </div> <!-- /content -->

        <div id="modal_copyplan" class="modal fade">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Kopiera skördeplan</h5>
                        <button type="button" class="close" data-dismiss="modal">&times;</button>
                    </div>
                    <div class="modal-body">
                        <ValidationObserver ref="copyform">
                            <form @submit.prevent="copyPlan">
                                <div class="form-group">
                                    <label>Startdatum: <span class="text-danger">*</span></label>
                                    <ValidationProvider vid="start_date" name="Startdatum" rules="required" v-slot="{ errors }">
                                        <v-date-picker
                                            v-model="copy_start_date"
                                            mode="date"
                                            :locale="{ id: 'sv', firstDayOfWeek: 2, masks: { weekdays: 'WW', input: 'YYYY-MM-DD' } }"
                                            :disabled-dates='{ weekdays: [1, 3, 4, 5, 6, 7] }'
                                            :min-date="copy_min_date"
                                            :model-config="{ type: 'string', mask: 'YYYY-MM-DD' }"
                                            show-iso-weeknumbers
                                        >
                                            <template v-slot="{ inputValue, togglePopover }">
                                                <input class="form-control" :value="inputValue" @focus="togglePopover()" v-bind:class="{ 'border-danger':errors[0] }">
                                            </template>
                                        </v-date-picker>
                                        <span class="validation-invalid-label">{{ errors[0] }}</span>
                                    </ValidationProvider>
                                </div>
                            </form>
                        </ValidationObserver>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-light" data-dismiss="modal">Stäng</button>
                        <limitless-button ref="btncopyplan" type="button" @click="copyPlan" :busy="busy_saving" icon="icon-floppy-disk">Spara kopia</limitless-button>
                    </div>
                </div>
            </div>
        </div> <!-- /modal -->
    </div>
</template>

<script>
import apiClient from '@services/ApiClient';
import LimitlessPageHeader from '@components/Limitless/LimitlessPageHeader';
import LimitlessButton from '@components/Limitless/LimitlessButton';
import { getError } from '@services/Helpers';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import vSelect from 'vue-select';
import Vue from 'vue';

export default {
    name: 'HarvestPlanEdit',
    components: {
        LimitlessButton,
        LimitlessPageHeader,
        VDatePicker,
        vSelect,
    },
    props: {
        planId: {
            type: Number,
            default: 0,
        },
        crops: {
            type: Array,
            required: true,
        },
        units: {
            type: Array,
            required: true,
        },
    },
    data: () => ({
        busy_loading: true,
        busy_saving: false,
        plan: null,
        unit: null,
        copy_start_date: '',
        copy_min_date: null,
        add_crop: null,
    }),
    mounted() {
        this.getPlan();
    },
    methods: {
        getPlan() {
            this.busy_loading = true;
            apiClient.get('/vue/harvestplans/' + this.planId)
                .then((response) => {
                    this.plan = response.data.plan;
                    this.unit = response.data.unit;
                })
                .finally(() => {
                    this.busy_loading = false;
                });
        },
        async savePlan() {
            const isValid = await this.$refs.planform.validate();
            if (isValid) {
                this.busy_saving = true;
                await apiClient.post('/vue/harvestplans/' + this.planId + '', { ...this.plan, unit_id: this.unit.id })
                    .then((response) => {
                        if (this.planId !== response.data.plan.id) {
                            window.location.href = '/harvestplans/' + response.data.plan.id;
                        }
                        this.plan = response.data.plan;
                        this.unit = response.data.unit;
                        this.$refs.btnsaveplan.triggerSaved();
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.planform.setErrors(parsederror);
                        }
                        this.$refs.btnsaveplan.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async copyPlan() {
            const isValid = await this.$refs.copyform.validate();
            if (isValid) {
                this.busy_saving = true;
                const plan = JSON.parse(JSON.stringify(this.plan));
                plan.start_date = this.copy_start_date;
                await apiClient.post('/vue/harvestplans/0', plan)
                    .then((response) => {
                        window.location.href = '/harvestplans/' + response.data.plan.id;
                    })
                    .catch((error) => {
                        const parsederror = getError(error);
                        if (typeof parsederror === 'object') {
                            this.$refs.copyform.setErrors(parsederror);
                        }
                        this.$refs.btncopyplan.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        deletePlan() {
            // eslint-disable-next-line no-restricted-globals, no-alert
            if (confirm('Are you sure you want to delete this harvest plan?')) {
                this.busy_saving = true;
                apiClient.delete('/vue/harvestplans/' + this.planId)
                    .then(() => {
                        document.location.href = '/harvestplans';
                        this.busy_saving = false;
                    })
                    .catch(() => {
                        this.$refs.btndeleteplan.triggerFailed();
                    })
                    .finally(() => {
                        this.busy_saving = false;
                    });
            }
        },
        async showModalCopyPlan() {
            const isValid = await this.$refs.planform.validate();
            if (isValid) {
                this.copy_start_date = this.plan.start_date;
                const dateparts = this.plan.start_date.split('-');
                this.copy_min_date = null;
                if (dateparts) {
                    this.copy_min_date = new Date(dateparts[0], dateparts[1] - 1, dateparts[2]);
                    this.copy_min_date.setDate(this.copy_min_date.getDate() + 7);
                    this.copy_start_date = this.copy_min_date.toLocaleString('sv-SE').substr(0, 10);
                }
                $('#modal_copyplan').modal('show');
            }
        },
        addCrop(crop, layerId) {
            if (crop) {
                if (!this.plan.crops_weekplan[layerId]) {
                    Vue.set(this.plan.crops_weekplan, layerId, {});
                }
                if (!this.plan.crops_weekplan[layerId][crop.id]) {
                    Vue.set(this.plan.crops_weekplan[layerId], crop.id, {
                        1: 0,
                        2: 0,
                        3: 0,
                        4: 0,
                        5: 0,
                        6: 0,
                        7: 0,
                    });
                }
            }
            this.add_crop = null;
            this.$forceUpdate();
        },
    },
    computed: {
        plan_name() {
            return (this.plan && this.unit ? this.unit.name + ': ' + (this.plan.id > 0 ? this.plan.start_date : 'Ny plan') : 'Ny plan');
        },
        layer_crop_count() {
            const obj = {};
            Object.keys(this.plan.crops_weekplan).forEach((layer) => {
                obj[layer] = {
                    count: 0,
                    crops: {},
                    days: {
                        1: 0,
                        2: 0,
                        3: 0,
                        4: 0,
                        5: 0,
                        6: 0,
                        7: 0,
                    },
                };
                Object.keys(this.plan.crops_weekplan[layer]).forEach((crop) => {
                    obj[layer].crops[crop] = 0;
                    Object.keys(this.plan.crops_weekplan[layer][crop]).forEach((weekday) => {
                        obj[layer].count += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                        obj[layer].crops[crop] += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                        obj[layer].days[weekday] += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                    });
                });
            });
            return obj;
        },
        total_crop_count() {
            const obj = {
                available_slots: 0,
                count: 0,
                crops: {},
                days: {
                    1: 0,
                    2: 0,
                    3: 0,
                    4: 0,
                    5: 0,
                    6: 0,
                    7: 0,
                },
            };
            Object.keys(this.plan.crops_weekplan).forEach((layer) => {
                Object.keys(this.plan.crops_weekplan[layer]).forEach((crop) => {
                    obj.crops[crop] = 0;
                    Object.keys(this.plan.crops_weekplan[layer][crop]).forEach((weekday) => {
                        obj.count += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                        obj.crops[crop] += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                        obj.days[weekday] += (this.plan.crops_weekplan[layer][crop][weekday] ? parseInt(this.plan.crops_weekplan[layer][crop][weekday], 10) : 0);
                    });
                });
            });

            this.unit.layers.forEach((layer) => {
                obj.available_slots += (layer.gutters * layer.plugs);
            });
            obj.available_slots /= (this.unit.days_in_system / 7);
            return obj;
        },
        weekday_crop_sowdates() {
            const obj = {};
            if (this.plan && this.unit) {
                const dateparts = this.plan.start_date.split('-');
                if (dateparts) {
                    const today = new Date();
                    const date = new Date(dateparts[0], dateparts[1] - 1, dateparts[2]);

                    this.crops.forEach((crop) => {
                        const sowDate = new Date(date.valueOf());
                        sowDate.setDate(sowDate.getDate() - (crop.germination_days + crop.nursery_days + crop.system_days));
                        obj[crop.id] = {};
                        obj[crop.id][1] = { sow_date: new Date(sowDate.valueOf()), sow_date_passed: false };
                        if (obj[crop.id][1].sow_date < today) {
                            obj[crop.id][1].sow_date_passed = true;
                        }
                        obj[crop.id][2] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][2].sow_date < today) {
                            obj[crop.id][2].sow_date_passed = true;
                        }
                        obj[crop.id][3] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][3].sow_date < today) {
                            obj[crop.id][3].sow_date_passed = true;
                        }
                        obj[crop.id][4] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][4].sow_date < today) {
                            obj[crop.id][4].sow_date_passed = true;
                        }
                        obj[crop.id][5] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][5].sow_date < today) {
                            obj[crop.id][5].sow_date_passed = true;
                        }
                        obj[crop.id][6] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][6].sow_date < today) {
                            obj[crop.id][6].sow_date_passed = true;
                        }
                        obj[crop.id][7] = { sow_date: new Date(sowDate.setDate(sowDate.getDate() + 1).valueOf()), sow_date_passed: false };
                        if (obj[crop.id][7].sow_date < today) {
                            obj[crop.id][7].sow_date_passed = true;
                        }
                    });
                }
            }
            return obj;
        },
    },
};
</script>
